import { useMemo } from "react";
import { Button } from "@/components/ui/button";
import { useListCustomers } from "@/lib/api/customer";
import { TtOrderMetadata, TtOrderSnapshot } from "@/lib/api/risk/brokerage/tt";
import { SelectOptionItem } from "@/lib/models/ui";
import { CustomerSplitsField } from "./CustomerSplitsField";
import { CropYearField } from "./CropYearField";
import { OrderTypeField } from "./OrderTypeField";
import { useAttachOrderForm } from "./useAttachOrderForm";
import { OtcMode } from "./types";

interface AttachOrderFormProps {
  guidOrderId: string;
  snapshot: TtOrderSnapshot;
  existingMetadata: TtOrderMetadata | null;
  /** Called after a successful save. Parent decides what to do (e.g. keep panel open). */
  onSaved?: () => void;
}

export function AttachOrderForm({
  guidOrderId,
  snapshot,
  existingMetadata,
  onSaved,
}: AttachOrderFormProps) {
  // NOTE: do not change the customer hook params or share with other callsites
  // — different params return different shapes / permissions.
  const customersResult = useListCustomers({ minimal: true });

  const allCustomers = customersResult.data?.data ?? [];

  const form = useAttachOrderForm({
    guidOrderId,
    snapshot,
    existingMetadata,
    customers: allCustomers.map((c: { id: string; long_name?: string; short_name?: string; hasRiskProfile?: boolean }) => ({
      id: c.id,
      longName: c.long_name || c.short_name || "",
      hasRiskProfile: c.hasRiskProfile,
    })),
    onSaved,
  });

  const customerOptions: SelectOptionItem<string>[] = useMemo(() => {
    return allCustomers
      .filter((c: { hasRiskProfile?: boolean }) => {
        if (form.values.isNonClient) return !c.hasRiskProfile;
        return !!c.hasRiskProfile;
      })
      .map((c: { id: string; long_name?: string; short_name?: string }) => ({
        value: c.id,
        label: c.long_name || c.short_name || "",
      }));
  }, [allCustomers, form.values.isNonClient]);

  const suggestionCustomerLabel = useMemo(() => {
    if (!form.suggestions.customerId) return undefined;
    const opt = allCustomers.find((c: { id: string }) => c.id === form.suggestions.customerId);
    return (opt as { long_name?: string; short_name?: string } | undefined)?.long_name
      ?? (opt as { short_name?: string } | undefined)?.short_name;
  }, [form.suggestions.customerId, allCustomers]);

  const showCustomerSuggestion =
    !!form.suggestions.customerId &&
    !form.values.customerSplits.some((s) => s.customer_id === form.suggestions.customerId);

  const showOrderTypeSuggestion =
    !!form.suggestions.orderType && form.values.orderType !== form.suggestions.orderType;

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    void form.submit();
  };

  return (
    <form onSubmit={handleSubmit} className="space-y-4 border rounded-md p-3">
      <h3 className="text-sm font-semibold">
        {existingMetadata ? "Update Metadata" : "Set Metadata"}
      </h3>

      <CustomerSplitsField
        splits={form.values.customerSplits}
        totalQuantity={form.totals.totalQuantity}
        lhcRemainder={form.totals.lhcRemainder}
        customerOptions={customerOptions}
        customersLoading={customersResult.isLoading}
        isNonClient={form.values.isNonClient}
        shouldIgnore={form.values.shouldIgnore}
        error={form.errors.customerSplits}
        suggestionCustomerId={form.suggestions.customerId}
        suggestionCustomerLabel={suggestionCustomerLabel}
        showCustomerSuggestion={showCustomerSuggestion}
        onChangeQuantity={form.changeCustomerSplitQuantity}
        onChangeCustomer={form.changeCustomerSplitCustomer}
        onAdd={form.addCustomerSplit}
        onRemove={form.removeCustomerSplit}
        onApplyCustomerSuggestion={form.applyCustomerSuggestion}
        onChangeIsNonClient={(v) => form.setField("isNonClient", v)}
        onChangeShouldIgnore={(v) => form.setField("shouldIgnore", v)}
      />

      <CropYearField
        value={form.values.cropYear}
        error={form.errors.cropYear}
        onChange={(v) => form.setField("cropYear", v)}
      />

      <OrderTypeField
        orderType={form.values.orderType}
        otcMode={form.values.otcMode}
        otcContractType={form.values.otcContractType}
        errorOrderType={form.errors.orderType}
        errorOtcContractType={form.errors.otcContractType}
        suggestionOrderType={form.suggestions.orderType}
        showOrderTypeSuggestion={showOrderTypeSuggestion}
        onChangeOrderType={(v) => {
          form.setField("orderType", v);
          if (v !== "otc") {
            form.setField("otcMode", null as OtcMode | null);
            form.setField("otcContractType", "");
          }
        }}
        onChangeOtcMode={(mode) => {
          form.setField("otcMode", mode);
          form.setField("otcContractType", "");
        }}
        onChangeOtcContractType={(v) => form.setField("otcContractType", v)}
        onApplyOrderTypeSuggestion={form.applyOrderTypeSuggestion}
      />

      <div className="flex justify-end">
        <Button type="submit" disabled={form.isSubmitting || !form.isValid}>
          {form.isSubmitting ? "Saving..." : "Save"}
        </Button>
      </div>
    </form>
  );
}
